import { formatMoney } from '../../lib/datetime';
import type { UpcomingMaturity } from '../../types/finance';
import { calendarDate } from './enteringFund.helpers';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;

/** Shown instead of a date when the fund could not say it. Never a guessed date. */
export const TERM_DATES_UNAVAILABLE =
  'Term dates are unavailable right now. Please check back shortly.';

export function openTermKey(term: UpcomingMaturity): string {
  return `${term.product_id}:${term.contribution_reference}`;
}

/** Whole days from the viewer's calendar day to a CALENDAR date (`yyyy-mm-dd`). */
export function daysUntilDay(day: string, today: Date = new Date()): number {
  const parts = DATE_ONLY.exec(day);
  if (!parts) return Number.NaN;
  const target = Date.UTC(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  const from = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((target - from) / DAY_MS);
}

/** Whole days from the viewer's calendar day to the day a term is COMPLETE: the day after
 *  its last day (`maturity_date`, as the fund says it). Capital still in on that day has
 *  earned the term; withdrawn on the last day or before, it has not — so the last day is
 *  never "today" for the client, it is the day before "complete". */
export function daysUntilComplete(lastDay: string, today: Date = new Date()): number {
  return daysUntilDay(lastDay, today) + 1;
}

/** The countdown to the day a term is complete. On its last day it reads "tomorrow". */
export function completeCountdown(days: number): string {
  if (days <= 1) return 'Complete tomorrow';
  return `Complete in ${days} days`;
}

/** The term's last day, said so that it cannot be read as a day the capital may leave. */
export function completeAfter(lastDay: string): string {
  return `Complete after ${calendarDate(lastDay)}`;
}

/** The terms whose last day is today or within `days` from today. */
export function termsEndingWithin(
  terms: readonly UpcomingMaturity[],
  days: number,
  today: Date = new Date(),
): UpcomingMaturity[] {
  return terms.filter((term) => {
    const left = daysUntilDay(term.maturity_date, today);
    return left >= 0 && left <= days;
  });
}

/** What a client reads, before withdrawing, about the term one contribution is in. */
export function openTermLine(term: UpcomingMaturity): string {
  return `${term.product_name}: the current term of ${formatMoney(term.amount, term.currency_code)} `
    + `is complete after ${calendarDate(term.maturity_date)}.`;
}
